import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

export default function StudentPickerModal({ 
  mode,
  instructorId,
  bookingData,
  onSelect,
  onClose
}) {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadStudents = async () => {
      if (!instructorId) {
        setError('Не указан инструктор');
        setLoading(false);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`/api/staff/instructor-students/${instructorId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (res.ok) {
          const data = await res.json();
          setStudents(data);
        } else {
          const errData = await res.json();
          setError(errData.message || 'Ошибка загрузки учеников');
        }
      } catch (err) {
        console.error('Ошибка загрузки учеников:', err);
        setError('Ошибка соединения с сервером');
      } finally {
        setLoading(false);
      }
    };

    loadStudents();
  }, [instructorId]);

  const getDaysColor = (days) => {
    if (days === null) return 'bg-gray-400 text-white';
    if (days <= 7) return 'bg-green-500 text-white';
    if (days <= 15) return 'bg-yellow-500 text-black';
    return 'bg-red-500 text-white';
  };

  const formatDays = (days) => {
    if (days === null) return 'Не было уроков';
    if (days === 0) return 'Сегодня';
    if (days === 1) return 'Вчера';
    if (days <= 4) return `${days} дня назад`;
    if (days <= 7) return `${days} дней назад`;
    if (days <= 30) return `${Math.floor(days / 7)} нед. назад`;
    return `${Math.floor(days / 30)} мес. назад`;
  };

  const title = mode === 'new-booking' 
    ? 'Выберите ученика для записи' 
    : 'Выберите ученика для переназначения';

  return (
    <div className="fixed inset-0 bg-black/60 z-[9999] flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[85vh] overflow-hidden shadow-2xl flex flex-col">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{title}</h2>
            <p className="text-sm text-gray-600 mt-1">
              {bookingData.date} • {bookingData.time} • {bookingData.place}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:bg-gray-100 rounded-full transition"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              <span className="ml-3 text-gray-600">Загрузка учеников...</span>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="text-4xl mb-3">⚠️</div>
              <p className="text-red-600">{error}</p>
            </div>
          ) : students.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-4xl mb-3">👥</div>
              <p className="text-gray-600">У этого инструктора пока нет учеников</p>
            </div>
          ) : (
            <div className="space-y-3">
              {students.map((student) => (
                <button
                  key={student.id}
                  onClick={() => onSelect(student)}
                  className="w-full p-4 bg-gray-50 hover:bg-blue-50 border border-gray-200 hover:border-blue-300 rounded-lg transition text-left group"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-lg font-semibold text-gray-900 truncate">
                          {student.fullName}
                        </span>
                        <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full flex-shrink-0">
                          {student.group || '—'}
                        </span>
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          <span className="font-medium text-green-700">
                            {student.lessonsLeft}
                          </span>
                          <span>занятий осталось</span>
                        </span>
                        
                        <span className="text-gray-400">•</span>
                        
                        <span className="flex items-center gap-1">
                          <span>Последний урок:</span>
                          <span className="font-medium">
                            {formatDays(student.daysSinceLastLesson)}
                          </span>
                        </span>
                      </div>
                    </div>

                    <div className={`px-3 py-1.5 rounded-lg text-sm font-bold flex-shrink-0 ${getDaysColor(student.daysSinceLastLesson)}`}>
                      {student.daysSinceLastLesson === null ? '—' : student.daysSinceLastLesson}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-3 flex-shrink-0">
          <div className="flex items-center justify-between text-xs text-gray-600">
            <span>Всего учеников: {students.length}</span>
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 bg-green-500 rounded"></span>
                0-7 дней
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 bg-yellow-500 rounded"></span>
                8-15 дней
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 bg-red-500 rounded"></span>
                &gt;15 дней
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}